import {
    Box, Chip, CircularProgress, FormControl, IconButton,
    MenuItem, Paper, Select, SelectChangeEvent, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Tooltip, Typography
} from "@mui/material"
import { Refresh } from "@mui/icons-material"
import { FC, useEffect, useState } from "react"
import PageTitle from "../Common/PageTitle"
import NoData from "../Common/NoData"
import { Apis } from "../../helpers"
import { TABLE_HEIGHT } from "../../configs"

interface UnlockRecordRow {
    _id: string
    lockId: number
    recordType?: number
    success?: boolean
    username?: string
    electricQuantity?: number
    lockDate: string
    serverDate?: string
}

type ResultFilter = 'success' | 'failed' | 'all'

interface StateProps {
    records: UnlockRecordRow[]
    loading: boolean
    refreshing: boolean
    result: ResultFilter
    page: number
    pageSize: number
    count: number
    sortOrder: 'asc' | 'desc'
}

const formatDateTime = (date?: string) =>
    date
        ? new Date(date).toLocaleString('en-KE', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
        : '—'

const AdminUnlockRecords: FC = () => {
    const [state, setState] = useState<StateProps>({
        records: [],
        loading: true,
        refreshing: false,
        result: 'all',
        page: 0,
        pageSize: 25,
        count: 0,
        sortOrder: 'desc'
    })

    const { records, loading, refreshing, result, page, pageSize, count, sortOrder } = state

    const fetchRecords = ({ newResult, newPage, newPageSize, newSortOrder, processVariable }: any) => {
        const Result = newResult || state.result
        const Page = newPage != null && newPage > -1 ? newPage : state.page
        const PageSize = newPageSize || state.pageSize
        const SortOrder = newSortOrder || state.sortOrder
        if (processVariable) setState(s => ({ ...s, [processVariable]: true }))
        const afterLoad = { loading: false, refreshing: false, result: Result, page: Page, pageSize: PageSize, sortOrder: SortOrder }
        Apis({ module: 'Locks', action: 'getUnlockRecords', result: Result, page: Page, pageSize: PageSize, sortOrder: SortOrder })
            .then((resp: any) => setState(s => ({ ...s, records: resp.data || [], count: resp.count ?? s.count, ...afterLoad })))
            .catch(() => setState(s => ({ ...s, ...afterLoad })))
    }

    const toggleSort = () => fetchRecords({ newPage: 0, newSortOrder: sortOrder === 'desc' ? 'asc' : 'desc', processVariable: 'loading' })

    useEffect(() => {
        fetchRecords({ processVariable: 'loading' })
        // eslint-disable-next-line
    }, [])

    return (
        <Box sx={{ pr: 5, height: '100%' }}>

            <PageTitle title="Unlock Records" />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    {count > 0 && (
                        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                            {count.toLocaleString()} total
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select
                            value={result}
                            onChange={(e: SelectChangeEvent) => fetchRecords({ newResult: e.target.value as ResultFilter, newPage: 0, processVariable: 'loading' })}
                            sx={{ fontSize: 13, borderRadius: 2 }}
                        >
                            <MenuItem sx={{ fontSize: 13 }} value="all">All Results</MenuItem>
                            <MenuItem sx={{ fontSize: 13 }} value="success">Successful</MenuItem>
                            <MenuItem sx={{ fontSize: 13 }} value="failed">Failed</MenuItem>
                        </Select>
                    </FormControl>
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={() => fetchRecords({ processVariable: 'refreshing' })} disabled={refreshing}>
                            {refreshing ? <CircularProgress size={18} /> : <Refresh sx={{ fontSize: 20 }} />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {!records.length || loading ?
                <NoData message="No unlock records found." loading={loading} /> :
                <Paper sx={{ width: '100%' }} elevation={0} variant="outlined">
                    <TableContainer sx={{ maxHeight: TABLE_HEIGHT }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Lock ID</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>
                                        <TableSortLabel active direction={sortOrder} onClick={toggleSort}>
                                            Unlocked At
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>User</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Result</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Received</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {records.map(r => (
                                    <TableRow hover key={r._id}>
                                        <TableCell sx={{ fontSize: 13 }}>{r.lockId}</TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>{formatDateTime(r.lockDate)}</TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>{r.username || '—'}</TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>{r.recordType ?? '—'}</TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>
                                            {r.success === undefined
                                                ? '—'
                                                : <Chip
                                                    size="small"
                                                    label={r.success ? 'Success' : 'Failed'}
                                                    color={r.success ? 'success' : 'error'}
                                                    variant="outlined"
                                                />}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>{formatDateTime(r.serverDate)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={count}
                        page={page}
                        onPageChange={(_, newPage) => fetchRecords({ newPage, processVariable: 'loading' })}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={e => fetchRecords({ newPage: 0, newPageSize: parseInt(e.target.value, 10), processVariable: 'loading' })}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </Paper>
            }
        </Box>
    )
}

export default AdminUnlockRecords
